import { Injectable } from '@angular/core';
import { MimeTypesEnum } from 'src/app/utils/enums';
import { CloudFoldersEnum } from 'src/app/utils/enums/cloud-folders.enum';

@Injectable({
  providedIn: 'root'
})
export class GdriveApiService {
  ROOT_FOLDER: string = 'root';
  private ITEM_FIELDS: string = 'id,name,mimeType,size,modifiedTime,parents,shared,shortcutDetails,capabilities';
  private LIST_FIELDS: string = `files(${this.ITEM_FIELDS})`;
  private ORDER_BY: string = 'folder,modifiedTime desc,name';
  private PAGE_SIZE: number = 1000;

  constructor() { }

  getSpecificFolder(name: string): string {
    return `(mimeType contains '${MimeTypesEnum.DriveFolder}' and '${this.ROOT_FOLDER}' in parents and name contains '${name}' and trashed = false)`;
  }

  queryFolder(id: string): string {
    return `('${id}' in parents) and (trashed = false)`;
    // return `
    //   (
    //   (mimeType contains '${MimeTypesEnum.DriveFolder}' and '${id}' in parents) or
    //   ('${id}' in parents) or
    //   (sharedWithMe and '${id}' in parents)
    //   ) and trashed = false`;
  }

  get queryMyDrive(): string {
    return `('${this.ROOT_FOLDER}' in parents) and (trashed = false)`
    // return `
    // (
    // (mimeType contains '${MimeTypesEnum.DriveFolder}' and '${this.ROOT_FOLDER}' in parents) or
    // ('${this.ROOT_FOLDER}' in parents)
    // ) and
    // (trashed = false)`;
  }

  get querySharedWithMe(): string {
    return `trashed = false and sharedWithMe`;
  }

  getSharedDrives(): Promise<gapi.client.drive.Drive[]> {
    return gapi.client.drive.drives.list()
      .then(async (response) => {
        return Promise.resolve(response.result.drives ? response.result.drives : []);
      },
        (err) => {
          return Promise.reject(err);
        });
  }

  async getFiles(currentFolderId: string, folderToShow?: CloudFoldersEnum): Promise<gapi.client.drive.File[] | undefined> {
    return gapi.client.drive.files.list({
      q: this.getQueryForGetFiles(currentFolderId, folderToShow),
      fields: this.LIST_FIELDS,
      pageSize: this.PAGE_SIZE,
      orderBy: this.ORDER_BY,
      supportsAllDrives: true,
      includeItemsFromAllDrives: true
    })
      .then(async (response) => {
        return Promise.resolve(response.result.files);
      },
        (err) => {
          return Promise.reject(err);
        });
  }

  async newFolder(name: string, folderId: string): Promise<gapi.client.drive.File> {
    const file: gapi.client.drive.File = {
      name,
      mimeType: MimeTypesEnum.DriveFolder,
      parents: [folderId]
    };

    return gapi.client.drive.files.create({
      resource: file,
      fields: 'id',
      supportsAllDrives: true,
    })
      .then(async (response) => {
        return Promise.resolve(response.result);
      },
        (err) => {
          return Promise.reject(err);
        });
  }

  async copyFile(fileId: string, parentId: string): Promise<gapi.client.drive.File> {
    const file: gapi.client.drive.File = {
      parents: [parentId]
    };

    return gapi.client.drive.files.copy({
      fileId,
      resource: file,
      supportsAllDrives: true,
    })
      .then(async (response) => {
        return Promise.resolve(response.result);
      },
        (err) => {
          return Promise.reject(err);
        });
  }

  async copyFolder(folderName: string, folderId: string, newParentId?: string): Promise<void> {
    // create a new folder at the chosen location
    const parentId = newParentId !== null ? newParentId : this.ROOT_FOLDER;

    if (!parentId) {
      return;
    }

    const newFolder = await this.newFolder(folderName, parentId);

    if (!newFolder || !newFolder.id) {
      return;
    }

    // get list of all files in the folder
    const files = await this.getFiles(folderId, undefined);

    if (!files) {
      return;
    }

    // copy all those files in the new folder
    const promises: Promise<any>[] = [];
    for (const file of files) {
      if (file.id) {
        promises.push(this.copyFile(file.id, newFolder.id));
      }
    }

    await Promise.all(promises).catch(async (error) => {
      // if something goes wrong, delete the created folder
      await this.deleteFile(newFolder.id || '');
      console.error(error);
    });
  }

  async moveFile(fileId: string, newParentId: string, oldParentId: string): Promise<gapi.client.drive.File> {
    return gapi.client.drive.files.update({
      fileId,
      addParents: newParentId,
      resource: null as unknown as gapi.client.drive.File,
      removeParents: oldParentId,
      supportsAllDrives: true,
    })
      .then(async (response) => {
        return Promise.resolve(response.result);
      },
        (err) => {
          return Promise.reject(err);
        });
  }

  async updateFile(fileId: string, file: gapi.client.drive.File): Promise<gapi.client.drive.File> {
    return gapi.client.drive.files.update({
      fileId,
      resource: file,
      supportsAllDrives: true,
    })
      .then(async (response) => {
        return Promise.resolve(response.result);
      },
        (err) => {
          return Promise.reject(err);
        });
  }

  async updateFileMetadata(fileId: string, metadata: Partial<gapi.client.drive.File>): Promise<gapi.client.drive.File> {
    return gapi.client.drive.files.update({
      fileId,
      resource: metadata,
      supportsAllDrives: true,
    }).then(async (response) => {
      return Promise.resolve(response.result);
    },
      (err) => {
        return Promise.reject(err);
      });
  }

  async deleteFile(fileId: string): Promise<gapi.client.drive.File> {
    const file: gapi.client.drive.File = {
      trashed: true
    };
    return await this.updateFile(fileId, file);
  }

  async getFile(fileId: string, fields?: string): Promise<gapi.client.drive.File> {
    return gapi.client.drive.files.get({
      fileId,
      fields: fields !== null ? fields : this.ITEM_FIELDS,
      supportsAllDrives: true,
    })
      .then(async (response) => {
        return Promise.resolve(response.result);
      },
        (err) => {
          return Promise.reject(err);
        });
  }

  async getFolderId(folderName: string): Promise<string | undefined> {
    const files = await this.getFilesByFolderName(folderName);

    if (files && files.length > 0) {
      return Promise.resolve(files[0].id);
    } else {
      const newFolder = await this.newFolder(folderName, this.ROOT_FOLDER);
      return Promise.resolve(newFolder.id);
    }
  }

  private getQueryForGetFiles(currentFolder: string, folderToShow?: CloudFoldersEnum): string {
    if (currentFolder === this.ROOT_FOLDER && folderToShow) {
      switch (folderToShow) {
        case CloudFoldersEnum.Myfiles: return this.queryMyDrive;
        case CloudFoldersEnum.Shared: return this.querySharedWithMe;
        default: return '';
      }
    }

    return this.queryFolder(currentFolder);
  }

  private getFilesByFolderName(folderName: string): Promise<gapi.client.drive.File[] | undefined> {
    return gapi.client.drive.files.list({
      q: this.getSpecificFolder(folderName),
      fields: this.LIST_FIELDS,
      pageSize: this.PAGE_SIZE,
      orderBy: this.ORDER_BY,
      supportsAllDrives: true,
    })
      .then(async (response) => {
        return Promise.resolve(response.result.files);
      },
        (err) => {
          return Promise.reject(err);
        });
  }
}
